'use client';

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface RevealProps {
  children: React.ReactNode;
  /** Delay before fade-in starts after entering viewport (ms) */
  delay?: number;
  /** Vertical translate distance while hidden (px) */
  distance?: number;
  /** Custom className for the wrapper */
  className?: string;
  /** Disable any animation (useful when content needs to render statically) */
  disabled?: boolean;
  /** Render as a specific element */
  as?: 'div' | 'section' | 'article' | 'li' | 'ul' | 'ol' | 'span';
  /** 'spring' = Signature-Moment mit Overshoot-Easing (Emil-Audit 2026-07-31).
   *  Sparsam einsetzen — nur fuer die 1-2 tragenden Produkt-Visuals. */
  variant?: 'default' | 'spring';
  /** 'late' = feuert erst, wenn das Element deutlich im Viewport steht
   *  (unteres Drittel ausgeblendet). Fuer Signature-Momente, die der User
   *  SEHEN soll — beim fruehen 12%-Anschnitt sind sie sonst schon vorbei,
   *  bevor er hingescrollt hat (User-Feedback 2026-07-31). */
  trigger?: 'default' | 'late';
}

/**
 * Reveal — Stripe/Linear-style scroll-triggered fade-in.
 *
 * - Uses IntersectionObserver (cheap, no scroll-listener spam)
 * - Respects prefers-reduced-motion via CSS motion-safe utilities
 * - Hidden state is opacity-0 + translate-y; revealed state is opacity-1 + translate-0
 * - Once revealed, observer disconnects (no flicker on re-scroll)
 */
export function Reveal({
  children,
  delay = 0,
  distance = 12,
  className,
  disabled = false,
  as: Component = 'div',
  variant = 'default',
  trigger = 'default',
}: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(disabled);

  useEffect(() => {
    if (disabled) return;
    const el = ref.current;
    if (!el) return;

    // Respect reduced-motion: snap to visible immediately
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional one-shot mount sync / external store subscription
      setRevealed(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setRevealed(true);
            observer.disconnect();
          }
        }
      },
      // 'late': unteres Viewport-Drittel zaehlt nicht — threshold bewusst
      // niedrig (0.25), damit auch Elemente hoeher als der Viewport sicher
      // triggern (intersectionRatio kann bei denen nie hoch werden).
      trigger === 'late'
        ? { threshold: 0.25, rootMargin: '0px 0px -30% 0px' }
        : { threshold: 0.12, rootMargin: '0px 0px -60px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [disabled, trigger]);

  return (
    <Component
      ref={ref as never}
      // Kinder können per CSS auf den Reveal reagieren:
      // [data-revealed] .my-stagger-item { animation: … }
      data-revealed={revealed ? '' : undefined}
      className={cn(
        // Nur opacity/transform transitionieren (transition-all riss Farb-/
        // Layoutänderungen im Wrapper über 700ms mit); strong ease statt
        // builtin ease-out — crisp statt floaty (Emil-Review 2026-07-31).
        variant === 'spring'
          ? 'motion-safe:transition-[opacity,transform] motion-safe:duration-[600ms] motion-safe:ease-[cubic-bezier(.34,1.56,.64,1)]'
          : 'motion-safe:transition-[opacity,transform] motion-safe:duration-[550ms] motion-safe:ease-[cubic-bezier(0.16,1,0.3,1)]',
        revealed
          ? 'opacity-100 translate-y-0'
          : 'motion-safe:opacity-0',
        className,
      )}
      style={
        revealed
          ? { transitionDelay: `${delay}ms` }
          : {
              transform: `translateY(${distance}px)`,
              transitionDelay: `${delay}ms`,
            }
      }
    >
      {children}
    </Component>
  );
}
